import { recruiterCandidatePhotoUrl } from "../api";

export function normalizeOpenPosition(j, idx = 0) {
  const title = String(j?.title || j?.role || j?.name || "").trim();
  const skills = Array.isArray(j?.skills) ? j.skills.join(", ") : String(j?.skills || "");
  return {
    id: j?.id || `open_${idx}_${title.toLowerCase().replace(/\s+/g, "_")}`,
    title,
    role: title,
    company: j?.company || j?.company_name || "",
    location: j?.location || "",
    summary: j?.summary || j?.description || "",
    skills,
    url: j?.url || j?.applyUrl || j?.apply_url || "",
    source: j?.source || j?.ats || "",
    dummy: Boolean(j?.dummy)
  };
}

export function isDummyOpenPosition(j) {
  if (!j) return true;
  if (j.dummy === true) return true;
  return false;
}

export function splitSkillList(text) {
  return String(text || "")
    .split(/[,;/|]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Parse LinkedIn-style headline: "Role @ Company | skill | skill, skill" */
export function parseRecruiterHeadline(text) {
  const raw = String(text || "").replace(/\s+/g, " ").trim();
  if (!raw) return { title: "", company: "", skills: [], headline: "" };

  const segments = raw.split(/\s*[|]\s*/).map((s) => s.trim()).filter(Boolean);
  let title = "";
  let company = "";
  const skillChunks = [];

  if (segments.length) {
    const first = segments[0];
    const atIdx = first.indexOf("@");
    if (atIdx >= 0) {
      title = first.slice(0, atIdx).trim();
      company = first.slice(atIdx + 1).trim();
    } else {
      title = first;
    }
    skillChunks.push(...segments.slice(1));
  }

  if (!skillChunks.length && !company) {
    skillChunks.push(raw);
    title = "";
  }

  const skills = [...new Set(
    skillChunks
      .flatMap((chunk) => splitSkillList(chunk))
      .map((s) => s.trim())
      .filter((s) => s.length >= 2)
  )];

  return {
    title: title.replace(/\s+/g, " ").trim(),
    company: company.replace(/\s+/g, " ").trim(),
    skills,
    headline: raw
  };
}

export function linkedInSkillSearchUrl({ title, company, skills } = {}) {
  const skillToks = (Array.isArray(skills) ? skills : splitSkillList(skills))
    .map((s) => String(s || "").trim())
    .filter(Boolean)
    .slice(0, 5);
  const parts = [...skillToks];
  if (company) parts.push(String(company).trim());
  else if (title && !skillToks.length) parts.push(String(title).trim());
  const keywords = parts.filter(Boolean).join("*").trim() || "developer";
  return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(keywords)}`;
}

export function splitSearchValues(text) {
  return String(text || "")
    .split(/[,;|]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function parseNeedYearList(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n >= 0))];
  }
  const fromText = splitSearchValues(value).map((s) => Number(s)).filter((n) => Number.isFinite(n) && n >= 0);
  if (fromText.length) return [...new Set(fromText)];
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? [n] : [];
}

export function openingSearchBlob(job) {
  return `${job.title || ""} ${job.role || ""} ${job.summary || ""} ${job.skills || ""} ${job.location || ""}`.toLowerCase();
}

export function openingLocationMatches(jobLoc, want) {
  const loc = String(jobLoc || "").trim().toLowerCase();
  let w = String(want || "").trim().toLowerCase();
  if (!w || w === "india" || w === "anywhere") return true;
  const remoteWanted = /remote|hybrid|wfh|work from home/.test(w);
  w = w.replace(/remote|hybrid|wfh|work from home/gi, " ").replace(/[,/]+/g, " ").replace(/\s+/g, " ").trim();
  if (remoteWanted && /remote|hybrid|wfh|work from home/.test(loc)) {
    if (!w) return true;
  }
  if (!w) return true;
  if (!loc || loc === "india") return true;
  if (loc.includes(w) || (loc.length >= 4 && w.includes(loc))) return true;
  const aliases = {
    bangalore: ["bangalore", "bengaluru"],
    bengaluru: ["bangalore", "bengaluru"],
    mumbai: ["mumbai", "bombay"],
    chennai: ["chennai", "madras"],
    gurugram: ["gurugram", "gurgaon"],
    gurgaon: ["gurugram", "gurgaon"]
  };
  const key = w.replace(/[^a-z]/g, "");
  return (aliases[key] || [key]).some((alias) => alias && loc.includes(alias));
}

export function openingTitleMatches(jobTitle, wantTitle) {
  const norm = (s) => String(s || "").trim().toLowerCase().replace(/full\s*-?\s*stack/g, "fullstack").replace(/\s+/g, " ");
  const want = norm(wantTitle);
  if (!want || want === "open positions") return true;
  const hay = norm(jobTitle);
  if (hay.includes(want)) return true;
  const tokens = want.split(/\s+/).filter((t) => t.length >= 3);
  if (!tokens.length) return true;
  const hits = tokens.filter((t) => hay.includes(t)).length;
  return hits >= Math.max(1, Math.ceil(tokens.length * 0.5));
}

export function openingYears(job) {
  const blob = openingSearchBlob(job);
  if (/fresher|intern|graduate|entry.?level|trainee|0\s*[-–to]+\s*1/.test(blob)) return 0;
  const m = blob.match(/(\d+)\s*\+?\s*(?:years?|yrs?)/i);
  if (m) return Number(m[1]);
  if (/\b(senior|lead|principal|staff|architect|manager|consultant)\b/.test(blob)) return 5;
  return null;
}

export function openingMatchesNeed(job, need) {
  if (!job) return false;
  const titles = splitSearchValues(need.title);
  if (titles.length && !titles.some((t) => openingTitleMatches(job.title || job.role || "", t))) return false;
  const locations = splitSearchValues(need.location);
  if (locations.length && !locations.some((l) => openingLocationMatches(job.location, l))) return false;
  const exp = String(need.experience || "both").toLowerCase();
  const years = openingYears(job);
  const blob = openingSearchBlob(job);
  if (exp === "fresher") {
    if (years != null && years > 1) return false;
    if (/\b(senior|lead|principal|staff|architect)\b/.test(job.title || "")) return false;
  }
  if (exp === "experienced") {
    if (years != null && years < 2) return false;
    if (/fresher|intern|graduate|trainee/.test(blob)) return false;
  }
  const yearList = parseNeedYearList(need.minYears ?? need.experienceYearsMin);
  if (yearList.length === 1 && years != null && years < yearList[0]) return false;
  if (yearList.length > 1 && years != null && !yearList.includes(years)) return false;
  const required = splitSkillList(need.skills);
  if (required.length && blob.length > 48) {
    const hit = required.some((sk) => blob.includes(sk.toLowerCase()));
    if (!hit && !titles.length) return false;
  }
  return true;
}

export function candidateMatchesOpening(c, opening) {
  if (!opening) return true;
  const want = String(opening.title || opening.role || "").trim().toLowerCase();
  if (!want) return true;
  const hay = `${c.job_role || ""} ${c.title || ""}`.toLowerCase();
  return hay.includes(want) || want.includes(hay.replace(/\s+/g, " "));
}

export function stripVerifiedCandidateLabel(text) {
  return String(text || "")
    .replace(/\bverified\s+(candidate|professional|job seeker)s?\b/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

export function isPlaceholderCompanyLabel(text) {
  return /^\s*verified\s+(candidate|professional|job seeker)s?\s*$/i.test(String(text || "").trim());
}

export function displayPersonName(c) {
  const first = String(c?.firstName || "").trim();
  const middle = String(c?.middleName || c?.middle_name || "").trim();
  const last = String(c?.lastName || "").trim();
  if (first || last) return [first, middle, last].filter(Boolean).join(" ");
  return stripVerifiedCandidateLabel(c?.name || c?.candidate_name || c?.displayName || "");
}

export function givenFamilySearchTokens(c) {
  const firstRaw = String(c?.firstName || "").trim();
  const lastRaw = String(c?.lastName || "").trim();
  let first = (firstRaw.split(/\s+/).filter(Boolean)[0] || "").trim();
  let last = (lastRaw.split(/\s+/).filter(Boolean).slice(-1)[0] || "").trim();
  if (firstRaw || lastRaw) return { first, last };
  const tokens = String(c?.name || c?.candidate_name || "").trim().split(/\s+/).filter(Boolean);
  if (tokens.length && !/^people$/i.test(tokens[0])) {
    first = tokens[0] || "";
    last = tokens.length > 1 ? tokens[tokens.length - 1] : "";
  }
  return { first, last };
}

export function displayCompanyName(c) {
  const fields = [c?.current_company, c?.company, c?.company_name, c?.employer];
  for (const v of fields) {
    if (!v || isPlaceholderCompanyLabel(v)) continue;
    const cleaned = stripVerifiedCandidateLabel(v);
    if (cleaned && !/^verified$/i.test(cleaned)) return cleaned;
  }
  return "";
}

export function normalizeLiveCandidate(c) {
  const linkedIn = /linkedin/i.test(String(c.platform || c.source || c.profileUrl || c.linkedin_url || ""));
  const naukri = /naukri/i.test(String(c.platform || c.source || c.profileUrl || ""));
  const name = displayPersonName(c);
  const company = displayCompanyName(c);
  const roleRaw = stripVerifiedCandidateLabel(
    c.job_title || c.jobTitle || c.role || c.job_role || c.title || ""
  );
  const role = roleRaw && !isPlaceholderCompanyLabel(roleRaw) && !/verified\s+(candidate|professional)/i.test(roleRaw)
    ? roleRaw
    : "";
  let title = role || stripVerifiedCandidateLabel(c.title || "");
  if (!title || isPlaceholderCompanyLabel(title) || /verified\s+(candidate|professional)/i.test(String(title || ""))) {
    title = company || "";
  }
  let badgeText = c.badgeText || (c.source === "job_applied" ? "Job applied" : (naukri ? "Naukri candidate" : (c.platform === "LinkedIn" || linkedIn ? "LinkedIn" : (c.platform === "GitHub" ? "GitHub live" : "WIBIL candidate"))));
  if (/verified\s+(candidate|professional)/i.test(String(badgeText))) {
    badgeText = linkedIn ? "LinkedIn" : (naukri ? "Naukri candidate" : "WIBIL candidate");
  }
  return {
    ...c,
    name,
    candidate_name: name,
    displayName: name,
    firstName: c.firstName || "",
    middleName: c.middleName || c.middle_name || "",
    lastName: c.lastName || "",
    ageGroup: c.ageGroup || c.age_group || "",
    email: c.email || "",
    phone: c.phone || c.phoneNumber || c.phone_number || "",
    location: c.location || "",
    current_company: company,
    company,
    company_name: company,
    title: role || title,
    role: role || title,
    job_role: role || c.job_role || c.applied_for || "",
    job_title: role || c.job_title || c.jobTitle || "",
    jobTitle: role || c.jobTitle || c.job_title || "",
    total_experience: c.total_experience || c.experienceYears || "",
    experienceYears: c.experienceYears || c.total_experience || "",
    experience: c.experience || c.experience_label || "",
    profile_summary: c.profile_summary || c.summary || c.bio || "",
    bio: c.bio || c.profile_summary || "",
    github_username: c.github_username || c.username || "",
    trust_score: c.trust_score || c.trustScore || null,
    strengths: c.strengths || (Array.isArray(c.skills) ? c.skills.join(", ") : (Array.isArray(c.requiredSkills) ? c.requiredSkills.join(", ") : (Array.isArray(c.extractedSkills) ? c.extractedSkills.join(", ") : ""))),
    linkedin_url: c.linkedin_url || (/linkedin\.com/i.test(String(c.profileUrl || "")) ? c.profileUrl : "") || "",
    github_url: c.github_url || (/github\.com/i.test(String(c.profileUrl || "")) ? c.profileUrl : "") || "",
    profileUrl: c.profileUrl || c.linkedin_url || c.github_url || "",
    imageUrl: c.imageUrl || c.photo || c.avatarUrl || c.avatar_url || c.profile_photo || c.profilePhoto || "",
    badgeText
  };
}

export function candidatePhotoUrl(c) {
  const url = String(c?.imageUrl || c?.photo || c?.avatarUrl || c?.avatar_url || c?.profile_photo || c?.profilePhoto || "").trim();
  if (!url || /unsplash\.com|placeholder|dummy|example\.com/i.test(url)) return "";
  // Profile pages are not images — proxying them always fails (LinkedIn blocks scrape).
  if (/linkedin\.com\/in\//i.test(url) || /github\.com\/[^/]+\/?$/i.test(url)) return "";
  return url;
}

export function needsCandidatePhotoProxy(url) {
  const u = String(url || "");
  if (!u || /linkedin\.com\/in\//i.test(u)) return false;
  return /(?:^https:\/\/)?(?:[^/\s]*\.)?(licdn\.com|media\.licdn\.com|googleusercontent\.com|ggpht\.com|gstatic\.com)/i.test(u);
}

export function candidateDisplayPhotoSrc(c) {
  const raw = candidatePhotoUrl(c);
  if (raw && needsCandidatePhotoProxy(raw)) {
    return recruiterCandidatePhotoUrl({ imageUrl: raw });
  }
  if (raw) return raw;
  // No stored avatar: show initials. Do not call candidate-photo?profile= (console 404 spam).
  return "";
}

export function candidateInitials(c) {
  const parts = String(c?.name || c?.candidate_name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export function isNaukriCandidate(c) {
  const plat = String(c?.platform || "").toLowerCase();
  const src = String(c?.source || "").toLowerCase();
  const url = String(c?.profileUrl || c?.linkedin_url || c?.applyUrl || c?.jobUrl || "").toLowerCase();
  return plat === "naukri" || src.includes("naukri") || url.includes("naukri.com");
}

export function isNaukriJobListingUrl(url) {
  const u = String(url || "").toLowerCase();
  if (!u.includes("naukri.com")) return false;
  return /job-listings|jobs-in-|\/jobs\/|jobdescription|company-jobs|recruiters?\//.test(u);
}

export function isLinkedInCandidate(c) {
  if (isNaukriCandidate(c)) return false;
  const plat = String(c?.platform || "").toLowerCase();
  const src = String(c?.source || "").toLowerCase();
  const url = String(c?.profileUrl || c?.linkedin_url || c?.applyUrl || "").toLowerCase();
  return plat === "linkedin" || src.includes("linkedin") || url.includes("linkedin.com");
}

export function linkedInPersonName(c) {
  return displayPersonName(c);
}

export function linkedInCompanyName(c) {
  return displayCompanyName(c);
}

export function isGitHubCandidate(c) {
  const plat = String(c?.platform || "").toLowerCase();
  const src = String(c?.source || "").toLowerCase();
  const url = String(c?.profileUrl || c?.github_url || "").toLowerCase();
  return plat === "github" || src.includes("github") || url.includes("github.com");
}

export function candidateSourceLabel(c) {
  if (String(c?.source || "").toLowerCase() === "job_applied" || String(c?.platform || "").toLowerCase() === "wibil apply") {
    return "Job applied";
  }
  if (isNaukriCandidate(c)) return "Naukri";
  if (isLinkedInCandidate(c)) return "LinkedIn";
  if (isGitHubCandidate(c)) return "GitHub";
  return String(c?.platform || c?.source || "WIBIL").trim() || "WIBIL";
}

export function isDummyCandidate(c) {
  if (!c) return true;
  if (c.dummy === true) return true;
  return false;
}

export function isPeopleCandidate(c) {
  if (!c || c.dummy || isDummyCandidate(c) || !(c.name || c.title)) return false;
  if (String(c.kind || "").toLowerCase() === "job") return false;
  const source = String(c.source || "").toLowerCase();
  const badge = String(c.badgeText || "").toLowerCase();
  const url = String(c.profileUrl || c.linkedin_url || c.applyUrl || c.jobUrl || "").toLowerCase();
  const id = String(c.id || "").toLowerCase();
  if (/indeed_jobs|linkedin_guest_jobs|google_cse_indeed|linkedin_company_jobs|indeed_html|indeed_search|jsonld_jobposting/.test(source)) return false;
  if (source === "google_cse") return false;
  if (isNaukriJobListingUrl(url)) return false;
  if (source === "naukri_search" || /naukri/i.test(String(c.platform || "")) || (/naukri\.com/.test(url) && !isNaukriJobListingUrl(url))) {
    return Boolean(String(c.name || "").trim());
  }
  if (/career_team|career_google_people|company career/.test(source + " " + String(c.platform || "").toLowerCase())) return true;
  if (/\bjob\b/.test(badge) && !/candidate|profile|github|people|naukri/.test(badge)) return false;
  if (/linkedin\.com\/jobs|indeed\.[^/]+\/(viewjob|jobs)/.test(url)) return false;
  if (id.startsWith("indeed_") || id.startsWith("li_job_")) return false;
  return true;
}

export function scrapedRecordsFromResult(res) {
  const fromList = Array.isArray(res?.candidates) ? res.candidates : [];
  return fromList.map(normalizeLiveCandidate).filter(isPeopleCandidate);
}

export function candidateRowId(c) {
  if (!c) return null;
  if (c.id != null && c.id !== "") return String(c.id);
  if (c.employeeId != null && c.employeeId !== "") return String(c.employeeId);
  if (c.employee_id != null && c.employee_id !== "") return String(c.employee_id);
  return c.name || c.candidate_name || null;
}

export function linkedInPeopleSearchUrl(c) {
  const { first, last } = givenFamilySearchTokens(c);
  const company = displayCompanyName(c) || String(c?.current_company || c?.company || "").trim();
  const keywords = [first, last, company].filter(Boolean).join("*").trim() || "developer";
  return `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(keywords)}`;
}

export function indeedPeopleSearchUrl(c) {
  const name = String(c?.name || "").trim();
  const company = String(c?.current_company || c?.company || "").trim();
  const q = [name, company].filter(Boolean).join(" ").trim() || "software engineer";
  return `https://resumes.indeed.com/search?q=${encodeURIComponent(q)}&l=India`;
}

export function githubPeopleSearchUrl(c) {
  const { first, last } = givenFamilySearchTokens(c);
  const q = [first, last].filter(Boolean).join("*").trim() || "developer";
  return `https://github.com/search?q=${encodeURIComponent(q)}&type=users`;
}

export function indeedJobsSearchUrl(role, location) {
  const q = String(role || "full stack developer").trim();
  const loc = String(location || "India").trim() || "India";
  return `https://in.indeed.com/jobs?q=${encodeURIComponent(q)}&l=${encodeURIComponent(loc)}`;
}

export function parseLinkedInProfileUrl(raw) {
  const text = String(raw || "");
  const m = text.match(/linkedin\.com\/in\/([^/?#\s]+)/i);
  if (!m) return "";
  const slug = decodeURIComponent(m[1]).replace(/\/+$/, "");
  if (!slug || /^(profile|feed|404)$/i.test(slug)) return "";
  return `https://www.linkedin.com/in/${slug}/`;
}

export function linkedInSlugFromUrl(raw) {
  const url = parseLinkedInProfileUrl(raw);
  const m = String(url || raw || "").match(/linkedin\.com\/in\/([^/?#\s]+)/i)
    || (/^[A-Za-z0-9][A-Za-z0-9._%-]{0,99}$/.test(String(raw || "").trim()) ? [null, String(raw).trim()] : null);
  if (!m) return "";
  const slug = decodeURIComponent(m[1]).replace(/\/+$/, "");
  if (!slug || /^(profile|feed|404)$/i.test(slug)) return "";
  return slug;
}

/** LinkedIn Contact info overlay for a profile (requires recruiter LinkedIn login). */
export function linkedInContactInfoUrl(raw) {
  const slug = linkedInSlugFromUrl(raw);
  if (!slug) return "";
  return `https://www.linkedin.com/in/${slug}/overlay/contact-info/`;
}

export function nameFromLinkedInSlug(url) {
  const slugRaw = linkedInSlugFromUrl(url) || String(url || "");
  const slug = slugRaw.replace(/-\d{5,}$/g, "");
  const parts = slug.split("-").map((p) => p.replace(/\d+$/g, "").trim()).filter((p) => p && !/^\d+$/.test(p) && p.length > 1);
  const titled = parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
  return {
    firstName: titled[0] || "",
    lastName: titled.slice(1).join(" ") || "",
    name: titled.join(" ")
  };
}

function normalizeExtractedPhone(rawPhone) {
  const cleaned = String(rawPhone || "").replace(/[^\d+]/g, "");
  if (!cleaned) return "";
  const digits = cleaned.replace(/\D/g, "");
  if (digits.length < 10 || digits.length > 15) return "";
  // Prefer E.164-ish if it started with +
  if (cleaned.startsWith("+")) return `+${digits}`;
  return digits;
}

/**
 * Parse pasted LinkedIn Contact info text and/or profile URL.
 * Typical paste from Contact info modal includes Email / Phone / Mobile labels.
 */
export function parseLinkedInContactPaste(text) {
  const raw = String(text || "");
  const url = parseLinkedInProfileUrl(raw);
  const emails = [...raw.matchAll(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi)].map((m) => m[0]);
  const labeledEmail =
    (raw.match(/(?:e-?mail|email address)\s*[:\-]?\s*([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i) || [])[1]
    || "";
  const email = labeledEmail || emails.find((e) => !/linkedin\.com$/i.test(e.split("@")[1] || "")) || emails[0] || "";

  const labeledPhone =
    (raw.match(/(?:mobile|phone|phone number|cellphone|cell)\s*[:\-]?\s*(\+?\d[\d\s\-().]{7,}\d)/i) || [])[1]
    || "";
  const phoneCandidates = [...raw.matchAll(/(?:\+?\d[\d\s\-().]{7,}\d)/g)].map((m) => m[0]);
  let phone = normalizeExtractedPhone(labeledPhone);
  if (!phone) {
    for (const candidate of phoneCandidates) {
      const n = normalizeExtractedPhone(candidate);
      if (n) {
        phone = n;
        break;
      }
    }
  }

  const names = url ? nameFromLinkedInSlug(url) : { firstName: "", lastName: "", name: "" };
  return {
    url,
    slug: linkedInSlugFromUrl(url || raw),
    email,
    phone,
    ...names
  };
}

export function parseGitHubProfileUrl(raw) {
  const text = String(raw || "").trim();
  if (!text) return "";
  if (!/[./]/.test(text) && /^[A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){0,38}$/.test(text)) {
    return `https://github.com/${text}`;
  }
  const m = text.match(/github\.com\/([A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){0,38})(?:\/|$|\?|#)/i);
  if (!m) return "";
  const username = m[1];
  const reserved = /^(search|topics|about|login|signup|settings|orgs|organizations|marketplace|explore|notifications|issues|pulls|stars|trending|new|features|enterprise|pricing|site|blog|events|collections|sponsors|codespaces|copilot|security|team|readme)$/i;
  if (reserved.test(username)) return "";
  return `https://github.com/${username}`;
}

export function githubUsernameFromUrl(raw) {
  const url = parseGitHubProfileUrl(raw);
  const m = String(url || "").match(/github\.com\/([^/?#]+)/i);
  return m ? m[1] : "";
}

export function nameFromGitHubUsername(username) {
  const slug = String(username || "").replace(/^@/, "");
  if (!slug) return { firstName: "", lastName: "", name: "" };
  const parts = slug.split(/[-_.]+/).map((p) => p.replace(/\d+$/g, "").trim()).filter((p) => p && !/^\d+$/.test(p) && p.length > 1);
  const titled = parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
  return {
    firstName: titled[0] || "",
    lastName: titled.slice(1).join(" ") || "",
    name: titled.join(" ") || slug
  };
}

export function parseGitHubContactPaste(text) {
  const raw = String(text || "");
  const url = parseGitHubProfileUrl(raw);
  const username = githubUsernameFromUrl(url || raw);
  const emailMatch = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  const phoneMatch = raw.match(/(?:\+?\d[\d\s\-()]{8,}\d)/);
  const names = username ? nameFromGitHubUsername(username) : { firstName: "", lastName: "", name: "" };
  return {
    url,
    username,
    email: emailMatch ? emailMatch[0] : "",
    phone: phoneMatch ? phoneMatch[0].replace(/[^\d+]/g, "") : "",
    ...names
  };
}

export function looksLikeGuessedLinkedInInUrl(url, c) {
  const raw = String(url || "");
  const m = raw.match(/linkedin\.com\/in\/([^/?#]+)/i);
  if (!m) return false;
  const slug = decodeURIComponent(m[1]).replace(/\/+$/, "").toLowerCase();
  if (!slug || /^(profile|404|feed)$/i.test(slug)) return true;
  const nameSlug = String(c?.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const coSlug = String(c?.current_company || c?.company || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  if (nameSlug && coSlug && (slug === `${nameSlug}-${coSlug}` || slug === `${coSlug}-${nameSlug}`)) return true;
  if (nameSlug && slug === `${nameSlug}-partner`) return true;
  return false;
}

export function candidatePublicLink(c) {
  const raw = String(c?.profileUrl || c?.linkedin_url || c?.applyUrl || "").trim();
  const withProto = raw && !/^https?:\/\//i.test(raw) ? `https://${raw}` : raw;
  const platform = String(c?.platform || c?.source || "").toLowerCase();
  if (/naukri\.com/i.test(withProto) || platform.includes("naukri")) {
    if (isNaukriJobListingUrl(withProto)) {
      return { href: withProto, label: "Open Naukri listing" };
    }
    return { href: withProto || "https://www.naukri.com/", label: "Open Naukri profile" };
  }
  if (/github\.com\//i.test(withProto) || platform.includes("github")) {
    return { href: withProto || `https://github.com/search?q=${encodeURIComponent(c?.name || "")}&type=users`, label: "Open GitHub profile" };
  }
  if (/indeed\./i.test(withProto) || platform.includes("indeed")) {
    if (/indeed\.(com|in)\/(viewjob|rc\/clk|jobs)/i.test(withProto)) {
      return { href: withProto, label: "Open Indeed job" };
    }
    return { href: indeedPeopleSearchUrl(c), label: "Search on Indeed" };
  }
  if (/linkedin\.com\/search\/results\/people/i.test(withProto)) {
    return { href: linkedInPeopleSearchUrl(c), label: "Search on LinkedIn" };
  }
  if (/linkedin\.com\/(jobs|talent|recruiter|company)\//i.test(withProto)) {
    const isJob = /\/jobs\//i.test(withProto);
    return { href: withProto, label: isJob ? "Open LinkedIn job" : "Open on LinkedIn" };
  }
  if (/linkedin\.com\/in\/[^/?#\s]+/i.test(withProto) && !looksLikeGuessedLinkedInInUrl(withProto, c)) {
    const m = withProto.match(/linkedin\.com\/in\/([^/?#]+)/i);
    const handle = decodeURIComponent(m[1]).replace(/\/+$/, "");
    return { href: `https://www.linkedin.com/in/${handle}/`, label: "Open LinkedIn profile" };
  }
  return { href: linkedInPeopleSearchUrl(c), label: "Search on LinkedIn" };
}

export function candidateBoardLinks(c) {
  const primary = candidatePublicLink(c);
  const links = [primary];
  const platform = String(c?.platform || c?.source || "").toLowerCase();
  const isJob = /job/i.test(String(c?.badgeText || c?.source || ""));
  if (isJob) return links;
  if (!platform.includes("indeed") && !/indeed\./i.test(primary.href)) {
    links.push({ href: indeedPeopleSearchUrl(c), label: "Search on Indeed" });
  }
  if (!platform.includes("linkedin") && !platform.includes("github") && !/linkedin\./i.test(primary.href)) {
    links.push({ href: linkedInPeopleSearchUrl(c), label: "Search on LinkedIn" });
  }
  return links;
}

export function wibilEmployeeIdFromRow(c) {
  if (!c) return null;
  const rawId = String(c.id || "");
  if (rawId.startsWith("db_") && /^\d+$/.test(rawId.slice(3))) return rawId.slice(3);
  if (rawId.startsWith("gh_") || rawId.startsWith("live_gh_") || rawId.startsWith("scraped_") || rawId.startsWith("li_") || rawId.startsWith("live_nk_") || rawId.startsWith("naukri_")) return null;
  const platform = String(c.platform || c.source || "").toLowerCase();
  if (platform.includes("github") || platform.includes("linkedin") || platform.includes("scraped")) return null;
  if (c.employeeId != null && /^\d+$/.test(String(c.employeeId)) && (rawId === String(c.employeeId) || rawId.startsWith("db_"))) {
    return String(c.employeeId);
  }
  if (c.employee_id != null && /^\d+$/.test(String(c.employee_id)) && (rawId === String(c.employee_id) || !rawId)) {
    return String(c.employee_id);
  }
  if (/^\d+$/.test(rawId) && !platform) return rawId;
  return null;
}

const NAME_HONORIFICS = new Set(["mr", "mrs", "ms", "miss", "dr", "prof", "sir", "smt"]);

export function personNameParts(name) {
  const tokens = String(name || "")
    .trim()
    .toLowerCase()
    .split(/[\s._-]+/)
    .map((t) => t.replace(/[^a-z]/g, ""))
    .filter((t) => t && !NAME_HONORIFICS.has(t));
  if (!tokens.length) return { first: "", last: "", middle: [], tokens };
  if (tokens.length === 1) return { first: tokens[0], last: "", middle: [], tokens };
  return { first: tokens[0], last: tokens[tokens.length - 1], middle: tokens.slice(1, -1), tokens };
}

export function normalizeGivenFamilyName(first, last) {
  const parts = personNameParts(`${first || ""} ${last || ""}`);
  const displayName = [first, last].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  return {
    first: parts.first,
    last: parts.last,
    displayName,
    identityName: [parts.first, parts.last].filter(Boolean).join(" ")
  };
}

export function samePersonName(a, b) {
  const left = personNameParts(a);
  const right = personNameParts(b);
  if (!left.first || !right.first) return false;
  if (left.first !== right.first) return false;
  if (!left.last || !right.last) return left.tokens.join("") === right.tokens.join("");
  return left.last === right.last;
}

export function namesMatch(a, b) {
  if (samePersonName(a, b)) return true;
  const left = String(a || "").trim().toLowerCase();
  const right = String(b || "").trim().toLowerCase();
  return Boolean(left && right && left === right);
}

function isConsumerEmail(email) {
  return /@(gmail|googlemail|yahoo|hotmail|outlook|live|icloud)\./i.test(String(email || ""));
}

function phoneDigits(value) {
  const d = String(value || "").replace(/\D+/g, "");
  return d.length > 10 ? d.slice(-10) : d;
}

export function samePersonRecord(a, b) {
  if (!a || !b) return false;
  const phoneA = phoneDigits(a.phone);
  const phoneB = phoneDigits(b.phone);
  if (phoneA.length >= 10 && phoneA === phoneB) return true;
  const emailA = String(a.email || "").trim().toLowerCase();
  const emailB = String(b.email || "").trim().toLowerCase();
  if (emailA && emailA === emailB) return true;
  return samePersonName(a.name || a.candidate_name, b.name || b.candidate_name);
}

function recordIdentityScore(row) {
  const email = String(row?.email || "");
  const name = String(row?.name || row?.candidate_name || "");
  return (row?.uan ? 40 : 0)
    + (row?.aadhaar_last4 || row?.id_verification_method ? 20 : 0)
    + (email && !isConsumerEmail(email) ? 25 : 0)
    + Math.min(12, name.length)
    + (Number(row?.trust_score) || 0) / 200;
}

export function mergeSamePersonCandidates(list) {
  const rows = Array.isArray(list) ? list.filter(Boolean) : [];
  const groups = [];
  rows.forEach((row) => {
    const group = groups.find((g) => samePersonRecord(g[0], row));
    if (group) group.push(row);
    else groups.push([row]);
  });
  return groups.map((group) => {
    const ranked = [...group].sort((a, b) => recordIdentityScore(b) - recordIdentityScore(a));
    const best = { ...ranked[0] };
    const emails = [...new Set(group.map((r) => String(r.email || "").trim().toLowerCase()).filter(Boolean))];
    const names = [...new Set(group.map((r) => String(r.name || r.candidate_name || "").trim()).filter(Boolean))];
    best.email = ranked.find((r) => r.email)?.email || best.email;
    best.email_aliases = emails.filter((e) => e !== String(best.email || "").trim().toLowerCase());
    if (names.length) {
      best.name = names.sort((a, b) => b.length - a.length)[0];
      best.candidate_name = best.name;
      best.displayName = best.name;
    }
    return best;
  });
}

export const SHORTLIST_STORAGE_KEY = "wibil_shortlisted_candidates";
export const SHORTLIST_UPDATED_EVENT = "wibil-shortlist-updated";

export function persistLocalShortlist(entry) {
  const next = {
    ...entry,
    candidate_name: String(entry?.candidate_name || entry?.name || "").trim() || "Sourced Candidate",
    name: String(entry?.name || entry?.candidate_name || "").trim() || "Sourced Candidate",
    status: entry?.status || "shortlisted"
  };
  try {
    const stored = JSON.parse(localStorage.getItem(SHORTLIST_STORAGE_KEY) || "[]");
    const nameKey = String(next.candidate_name).trim().toLowerCase();
    const idKey = String(next.employee_id || next.candidate_id || "").trim().toLowerCase();
    const updated = [
      next,
      ...stored.filter((s) => {
        const sName = String(s.candidate_name || s.name || "").trim().toLowerCase();
        const sId = String(s.employee_id || s.candidate_id || "").trim().toLowerCase();
        if (nameKey && sName && sName === nameKey) return false;
        if (idKey && sId && sId === idKey) return false;
        return true;
      })
    ];
    localStorage.setItem(SHORTLIST_STORAGE_KEY, JSON.stringify(updated));
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new CustomEvent(SHORTLIST_UPDATED_EVENT, { detail: next }));
    return updated;
  } catch (err) {
    console.warn("Local shortlist persist error:", err);
    return [next];
  }
}

export function buildLocalCandidateReport(c) {
  const id = candidateRowId(c);
  return {
    profile: {
      id,
      name: displayPersonName(c),
      title: isPlaceholderCompanyLabel(c.title) ? displayCompanyName(c) : (c.title || c.job_role || ""),
      current_company: displayCompanyName(c),
      email: c.email || "",
      phone: c.phone || "",
      location: c.location || "",
      experience: c.experience || c.experience_label || c.total_experience || "",
      strengths: c.strengths || (Array.isArray(c.skills) ? c.skills.join(", ") : ""),
      summary: c.profile_summary || c.summary || c.bio || "",
      linkedin_url: c.linkedin_url || (/linkedin\.com/i.test(String(c.profileUrl || "")) ? c.profileUrl : ""),
      github_url: c.github_url || (/github\.com/i.test(String(c.profileUrl || "")) ? c.profileUrl : ""),
      github_username: c.github_username || "",
      uan: c.uan || c.candidate_uan || null,
      id_verification_method: c.id_verification_method || c.source || c.platform || null
    },
    trust_score: { trust_score: c.trust_score || c.trustScore || 650, rating_count: 0, verified_source_count: 0 },
    employment_history: [],
    manager_reviews: [],
    verified_documents: [],
    promotion_timeline: [],
    certifications: [],
    awards: [],
    warnings: [],
    linkedin: c
  };
}
